import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Mail, Loader2, Building2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClinicaLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/clinica/login`, {
        email,
        password,
      });

      localStorage.setItem('clinica_token', response.data.token);
      localStorage.setItem('clinica_data', JSON.stringify(response.data.clinica));
      toast.success(`¡Bienvenido, ${response.data.clinica.nombre}!`);
      navigate('/clinica/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Credenciales inválidas');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <img src="/amplicare-logo.svg" alt="Amplicare Lab" className="h-10" />
            <Link
              to="/admin/login"
              className="text-sm text-slate-500 hover:text-[#505BB3] transition-colors"
            >
              Acceso Administrador
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Login Card */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
            {/* Card Header */}
            <div className="bg-[#505BB3] px-6 py-8 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white">Portal de Clínicas</h1>
              <p className="text-white/80 mt-1">Ingrese sus credenciales para acceder</p>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    <Mail className="w-4 h-4 inline mr-1.5" />
                    Correo Electrónico
                  </Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="clinica@ejemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    <Lock className="w-4 h-4 inline mr-1.5" />
                    Contraseña
                  </Label>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                    required
                  />
                </div>

                <div className="flex justify-end">
                  <Link
                    to="/clinica/forgot-password"
                    className="text-sm text-[#505BB3] hover:underline"
                  >
                    ¿Olvidó su contraseña?
                  </Link>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  data-testid="login-button"
                  className="w-full h-12 bg-[#505BB3] hover:bg-[#404A9C] text-white font-medium"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Iniciando sesión...
                    </>
                  ) : (
                    <>
                      Iniciar Sesión
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Card Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
              <p className="text-center text-sm text-slate-600">
                ¿No tiene cuenta?{' '}
                <a
                  href="https://www.amplicarelab.com/contacto"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#505BB3] font-medium hover:underline"
                >
                  Contáctanos
                </a>
              </p>
            </div>
          </div>

          {/* Help Text */}
          <p className="text-center text-sm text-slate-500 mt-6">
            Si tiene problemas para acceder, contacte al administrador del sistema.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-slate-500">
          © {new Date().getFullYear()} Amplicare Lab. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
}
